import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import type { Paginated, ThreadCounts, ThreadDetail, ThreadSummary } from '@rundown/types';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';

export type { ThreadDetail, ThreadSummary };

/** Mirrors THREAD_FILTERS in apps/api/src/modules/threads/threads.dto.ts. */
export type ThreadFilter =
  'all' | 'unread' | 'mentions' | 'chats' | 'issues' | 'following' | 'archived' | 'review';

const THREADS_PAGE_SIZE = 30;

function threadsPath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/threads`;
}

function threadPath(workspaceId: string, threadId: string): string {
  return `${threadsPath(workspaceId)}/${encodeURIComponent(threadId)}`;
}

export function threadsInfiniteQueryOptions(
  workspaceId: string,
  filter: ThreadFilter,
  search = '',
  limit = THREADS_PAGE_SIZE,
) {
  return infiniteQueryOptions({
    queryKey: [...queryKeys.threadList(workspaceId, filter, search), limit],
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ filter, limit: String(limit) });
      if (search !== '') params.set('q', search);
      if (pageParam !== null) params.set('cursor', pageParam);
      return api.get<Paginated<ThreadSummary>>(`${threadsPath(workspaceId)}?${params.toString()}`, signal);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

export function threadQueryOptions(workspaceId: string, threadId: string) {
  return queryOptions({
    queryKey: queryKeys.thread(workspaceId, threadId),
    queryFn: ({ signal }) => api.get<ThreadDetail>(threadPath(workspaceId, threadId), signal),
  });
}

export interface NewThreadInput {
  readonly body: unknown;
  readonly attachmentIds?: readonly string[];
  /** Vendor staff only — the server ignores it for anyone else. */
  readonly internal?: boolean;
}

export function createThread(workspaceId: string, input: NewThreadInput): Promise<ThreadDetail> {
  return api.post<ThreadDetail>(threadsPath(workspaceId), input);
}

export function markThreadRead(workspaceId: string, threadId: string): Promise<void> {
  return api.post<void>(`${threadPath(workspaceId, threadId)}/read`);
}

export function markAllThreadsRead(workspaceId: string): Promise<void> {
  return api.post<void>(`${threadsPath(workspaceId)}/read-all`);
}

export function keepAsChat(workspaceId: string, threadId: string): Promise<ThreadDetail> {
  return api.post<ThreadDetail>(`${threadPath(workspaceId, threadId)}/keep-as-chat`);
}

export function editThread(workspaceId: string, threadId: string, body: unknown): Promise<ThreadDetail> {
  return api.patch<ThreadDetail>(threadPath(workspaceId, threadId), { body });
}

export function deleteThread(workspaceId: string, threadId: string): Promise<void> {
  return api.delete<void>(threadPath(workspaceId, threadId));
}

export function threadCountsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: queryKeys.threadCounts(workspaceId),
    queryFn: ({ signal }) => api.get<ThreadCounts>(`${threadsPath(workspaceId)}/counts`, signal),
  });
}

export function archiveThread(workspaceId: string, threadId: string): Promise<void> {
  return api.post<void>(`${threadPath(workspaceId, threadId)}/archive`);
}

export function unarchiveThread(workspaceId: string, threadId: string): Promise<void> {
  return api.post<void>(`${threadPath(workspaceId, threadId)}/unarchive`);
}

export function markThreadUnread(workspaceId: string, threadId: string): Promise<void> {
  return api.post<void>(`${threadPath(workspaceId, threadId)}/unread`);
}

/** The first page of a thread search, for the ⌘K palette. */
export function threadSearchQueryOptions(workspaceId: string, search: string, limit: number) {
  return queryOptions({
    queryKey: [...queryKeys.threadList(workspaceId, 'palette', search), limit],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ filter: 'all', limit: String(limit) });
      if (search !== '') params.set('q', search);
      return api
        .get<Paginated<ThreadSummary>>(`${threadsPath(workspaceId)}?${params.toString()}`, signal)
        .then((page) => page.items);
    },
  });
}
